import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@shared/enums/permission.enum';
import { InterventionStore } from '../../../intervention.store';
import { InterventionTaskResponse } from '../../../intervention.model';
import { stageLabel } from '../../../intervention-stage';

type StageGroup = {
  stage: string;
  state: 'past' | 'current' | 'upcoming';
  tasks: InterventionTaskResponse[];
  done: number;
  total: number;
};

// The whole lifecycle's tasks, grouped by stage in the type's stage order. The API already returns every
// task (completed, pending and cancelled) so nothing here needs a separate request - tasks are loaded with the intervention.
@Component({
  selector: 'intervention-task-panel',
  imports: [...SharedMaterialUIModule],
  templateUrl: './intervention-task-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class InterventionTaskPanelComponent {
  protected readonly interventionStore = inject(InterventionStore);
  private readonly permissionService = inject(PermissionService);

  readonly interventionId = input.required<string>();

  protected readonly groups = computed<StageGroup[]>(() => {
    const stages = this.interventionStore.lifecycleStages();
    const tasks = this.interventionStore.tasks();
    const currentIndex = stages.indexOf(this.interventionStore.selected()?.status ?? '');

    // Cancelled leftovers from a previous type may name a stage this type does not have; they are not shown.
    return stages
      .map((stage, index): StageGroup => {
        const stageTasks = tasks
          .filter((task) => task.stage === stage)
          .sort((a, b) => a.displayOrder - b.displayOrder);
        const live = stageTasks.filter((task) => task.status !== 'Cancelled');

        return {
          stage,
          state: index < currentIndex ? 'past' : index === currentIndex ? 'current' : 'upcoming',
          tasks: stageTasks,
          done: live.filter((task) => task.status === 'Completed').length,
          total: live.length,
        };
      })
      .filter((group) => group.tasks.length > 0 || group.state === 'current');
  });

  // Completing a task requires the same permission as any other intervention edit - affiliates can
  // see the checklist (so they know what's outstanding) but never complete items on it themselves.
  protected canComplete(): boolean {
    return this.permissionService.hasPermission(Permission.InterventionsUpdate);
  }

  protected complete(task: InterventionTaskResponse): void {
    this.interventionStore.completeTask(this.interventionId(), task.id, null);
  }

  protected readonly label = stageLabel;
}
